// Analytics Event Bus
//
// This module provides the central event coordination system for analytics.
// It manages provider registration, event routing, and error handling.

import { logger } from '../utils/core/logger';
import type { AnalyticsConfig, AnalyticsContext, AnalyticsEvent, AnalyticsProvider } from './types';

// Some embedded/in-app browsers ship a Promise without allSettled.
// Fall back to Promise.all with per-promise catch to preserve "wait for all" semantics.
const settleAll = <T>(promises: Promise<T>[]): Promise<unknown> => {
    if (typeof Promise.allSettled === 'function') {
        return Promise.allSettled(promises);
    }
    return Promise.all(promises.map((p) => p.catch(() => undefined)));
};

export class AnalyticsEventBus {
    private providers: AnalyticsProvider[] = [];
    private globalContext: AnalyticsContext = {};
    private config: AnalyticsConfig;

    constructor(config: AnalyticsConfig = {}) {
        this.config = {
            debug: false,
            timeoutMs: 5000,
            continueOnError: true,
            ...config,
        };

        if (config.globalContext) {
            this.globalContext = { ...config.globalContext };
        }

        if (this.config.debug) {
            logger.debug('AnalyticsEventBus initialized with config:', this.config);
        }
    }

    /**
     * Register an analytics provider
     */
    public async registerProvider(provider: AnalyticsProvider): Promise<void> {
        try {
            // Set up context update callback if provider supports it
            if (
                'setContextUpdateCallback' in provider &&
                typeof provider.setContextUpdateCallback === 'function'
            ) {
                // biome-ignore lint/suspicious/noExplicitAny: provider may implement setContextUpdateCallback without it being in the AnalyticsProvider interface
                (provider as any).setContextUpdateCallback((context: Partial<AnalyticsContext>) => {
                    this.updateGlobalContext(context);
                });
            }

            // Initialize provider with current global context
            if (provider.initialize) {
                await provider.initialize(this.globalContext);
            }

            this.providers.push(provider);
            logger.debug(`Registered analytics provider: ${provider.name}`);

            if (this.config.debug) {
                logger.debug(`Total providers registered: ${this.providers.length}`);
                logger.debug(
                    'Provider names:',
                    this.providers.map((p) => p.name)
                );
            }
        } catch (error) {
            logger.error(`Failed to register provider ${provider.name}:`, error);
            if (!this.config.continueOnError) {
                throw error;
            }
        }
    }

    /**
     * Unregister an analytics provider
     */
    public async unregisterProvider(providerName: string): Promise<void> {
        const providerIndex = this.providers.findIndex((p) => p.name === providerName);
        if (providerIndex === -1) {
            logger.warn(`Provider ${providerName} not found for unregistration`);
            return;
        }

        const provider = this.providers[providerIndex];

        try {
            if (provider.destroy) {
                await provider.destroy();
            }
        } catch (error) {
            logger.error(`Error destroying provider ${providerName}:`, error);
        }

        this.providers.splice(providerIndex, 1);
        logger.debug(`Unregistered analytics provider: ${providerName}`);
    }

    /**
     * Update global analytics context
     *
     * Updates the global context and notifies all providers via updateContext()
     * (does NOT re-initialize to avoid infinite loops)
     *
     * Providers handle their own persistence (e.g., LocalStorageProvider persists to localStorage)
     */
    public updateGlobalContext(context: Partial<AnalyticsContext>): void {
        this.globalContext = { ...this.globalContext, ...context };

        if (this.config.debug) {
            logger.debug('Updated global analytics context:', this.globalContext);
        }

        // Notify all providers of context update (lightweight, no re-initialization)
        // Providers handle their own persistence and external integrations
        // (e.g., LocalStorageProvider, PostHogProvider, SentryProvider)
        this.providers.forEach((provider) => {
            try {
                if (provider.updateContext) {
                    provider.updateContext(context);
                }
            } catch (error) {
                logger.error(`Failed to update context for provider ${provider.name}:`, error);
            }
        });
    }

    /**
     * Track an analytics event
     */
    public async track(
        type: string,
        properties: Record<string, unknown> = {},
        category?:
            | 'user_action'
            | 'system'
            | 'conversion'
            | 'conversation'
            | 'navigation'
            | 'evaluation'
    ): Promise<void> {
        const event: AnalyticsEvent = {
            type,
            properties,
            context: { ...this.globalContext }, // personId is part of globalContext
            timestamp: Date.now(),
            category,
        };

        if (this.config.debug) {
            logger.debug(`Tracking event: ${type}`, { properties, category });
        }

        // Get providers that should receive this event
        const interestedProviders = this.providers.filter((provider) => {
            try {
                const isEnabled = provider.isEnabled();
                const passesFilter = provider.eventFilter(event);
                return isEnabled && passesFilter;
            } catch (error) {
                logger.error(`Error in eventFilter for provider ${provider.name}:`, error);
                return false; // Skip provider if filter fails
            }
        });

        if (interestedProviders.length === 0) {
            logger.debug(`No providers interested in event: ${type}`);
            return;
        }

        if (this.config.debug) {
            logger.debug(
                `Event ${type} will be sent to providers:`,
                interestedProviders.map((p) => p.name)
            );
        }

        // Send to all interested providers in parallel
        const trackingPromises = interestedProviders.map(async (provider) => {
            try {
                await this.trackWithTimeout(provider, event);
            } catch (error) {
                // Log error with context (logger.error automatically sends to Sentry in production)
                logger.error(`Provider ${provider.name} failed to track event ${type}`, error, {
                    provider: provider.name,
                    event_type: type,
                    category: event.category || 'unknown',
                    event_properties: event.properties,
                });

                if (!this.config.continueOnError) {
                    throw error;
                }
            }
        });

        // Wait for all providers to complete (or fail)
        await settleAll(trackingPromises);

        if (this.config.debug) {
            logger.debug(`Event ${type} tracking completed`);
        }
    }

    /**
     * Track event with timeout protection
     */
    private async trackWithTimeout(
        provider: AnalyticsProvider,
        event: AnalyticsEvent
    ): Promise<void> {
        const timeoutMs = this.config.timeoutMs || 5000;

        const timeoutPromise = new Promise<never>((_, reject) => {
            setTimeout(() => {
                reject(new Error(`Provider ${provider.name} timeout after ${timeoutMs}ms`));
            }, timeoutMs);
        });

        try {
            await Promise.race([provider.track(event), timeoutPromise]);
        } catch (error) {
            if (error instanceof Error && error.message.includes('timeout')) {
                logger.warn(`Provider ${provider.name} timed out tracking event ${event.type}`);
            }
            throw error;
        }
    }

    /**
     * Get list of registered provider names
     */
    public getProviderNames(): string[] {
        return this.providers.map((p) => p.name);
    }

    /**
     * Get provider by name
     */
    public getProvider(name: string): AnalyticsProvider | undefined {
        return this.providers.find((p) => p.name === name);
    }

    /**
     * Enable/disable a specific provider
     */
    public setProviderEnabled(name: string, enabled: boolean): void {
        const provider = this.getProvider(name);
        if (!provider) {
            logger.warn(`Provider ${name} not found`);
            return;
        }

        if ('setEnabled' in provider && typeof provider.setEnabled === 'function') {
            // biome-ignore lint/suspicious/noExplicitAny: provider may implement setEnabled without it being in the AnalyticsProvider interface
            (provider as any).setEnabled(enabled);
        } else {
            logger.warn(`Provider ${name} does not support enable/disable`);
        }
    }

    /**
     * Get current global context
     */
    public getGlobalContext(): AnalyticsContext {
        return { ...this.globalContext };
    }

    /**
     * Destroy the event bus and all providers
     */
    public async destroy(): Promise<void> {
        logger.debug('Destroying AnalyticsEventBus...');

        const destroyPromises = this.providers.map(async (provider) => {
            try {
                if (provider.destroy) {
                    await provider.destroy();
                }
            } catch (error) {
                logger.error(`Error destroying provider ${provider.name}:`, error);
            }
        });

        await settleAll(destroyPromises);
        this.providers = [];

        logger.debug('AnalyticsEventBus destroyed');
    }
}
